import { useState, useEffect } from 'react';
import { useAuth } from '../auth/index.js';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

async function adminFetch(path, method = 'GET') {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  if (res.status === 204) return null;
  return res.json();
}

export function Admin() {
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTab(tab);
  }, [tab]);

  async function loadTab(t) {
    setError(null);
    try {
      if (t === 'stats') setStats(await adminFetch('/admin/stats'));
      else if (t === 'users') setUsers(await adminFetch('/admin/users'));
      else if (t === 'feedback') setFeedback(await adminFetch('/admin/feedback'));
    } catch (err) {
      setError(err.message === '403' ? 'Admin access required' : 'Failed to load');
    }
  }

  if (error) {
    return (
      <div className="p-6 max-w-3xl">
        <h1 className="text-lg font-semibold text-slate-900 mb-4">Admin</h1>
        <p className="text-sm text-destructive-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <h1 className="text-lg font-semibold text-slate-900">Admin</h1>

      <div className="flex gap-1 border-b border-slate-200">
        {['stats', 'users', 'feedback'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t ? 'border-primary-600 text-primary-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'stats' && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ['Users', stats.totalUsers],
            ['Cards', stats.totalApps],
            ['Goals', stats.totalGoals],
            ['Feedback', stats.totalFeedback],
          ].map(([label, value]) => (
            <div key={label} className="rounded border border-slate-200 p-4">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="text-2xl font-bold text-slate-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && users && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">{users.total} users</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Share Link</th>
                </tr>
              </thead>
              <tbody>
                {users.users.map((user) => (
                  <tr key={user.userId} className="border-b border-slate-100">
                    <td className="py-2 pr-4 text-xs text-slate-600 font-mono">{user.email || user.userId.slice(0, 8)}</td>
                    <td className="py-2 pr-4 text-slate-700">{user.displayName || '—'}</td>
                    <td className="py-2 pr-4">{user.hasShareLink ? <span className="text-green-600 text-xs">✓ Active</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'feedback' && feedback && (
        <div className="space-y-3">
          {feedback.feedback.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No feedback yet</p>
          ) : (
            feedback.feedback.map((item, i) => (
              <div key={i} className="rounded border border-slate-200 p-3">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs text-slate-500">{item.email}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400">{new Date(item.createdAt).toLocaleString()}</span>
                    <button
                      onClick={async () => {
                        await adminFetch(`/admin/feedback/${encodeURIComponent(item.sk)}`, 'DELETE');
                        setFeedback({ feedback: feedback.feedback.filter((f) => f.sk !== item.sk) });
                      }}
                      className="text-[10px] text-destructive-600 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="text-sm text-slate-700">{item.message}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
